import React, {useEffect, useRef, useState} from 'react';
import * as THREE from 'three';
import HoloLensVisorOverlay from './HoloLensVisorOverlay';
import {TooltipKeys} from "../tooltips/TooltipKeys";
import {
	GizmoMoveIcon,
	GizmoNoneIcon,
	HomeIcon,
	Rotate3dIcon,
	SpaceLocalIcon,
	SpaceWorldIcon,
	TargetIcon
} from '../Iconset';
import ButtonGroup from '../layout/ButtonGroup';
import Button from '../layout/Button';
import Spacer from '../layout/Spacer';
import Panel from '../layout/Panel';
import {CustomEventTypes, GizmoModes, LayoutOrientations, Space} from '../../globals';
import SetModelTrackerPoseAction from '../../actions/SetModelTrackerPoseAction';
import ModalManager from '../layout/ModalManager';
import Engine from '../../classes/Engine';
import Input from '../../classes/Input';
import WorkspaceManager from '../../classes/WorkspaceManager';
import {assert} from '../../classes/utilities/ErrorHandling';
import ActionManager from '../../classes/ActionManager';
import SceneCameraController from "../../classes/SceneCameraController";
import ARTracker from "../../classes/ARTracker";
import { GrapesDeviceTypes, IDisplayDeviceInfo } from '../../interfaces/VisageUI';
import { useTypedSelector } from '../../state/hooks';
import useForceUpdate from '../reactUtils/useForceUpdate';
import useEventListener from '../reactUtils/useEventListener';

interface ModelTrackerPoseEditorProps {
	tracker: ARTracker;
	getEditPoseModalId: () => string;
}

// Initialize global variables with proper types
const frustum: THREE.Frustum = new THREE.Frustum();
const projectionMat: THREE.Matrix4 = new THREE.Matrix4();
const cameraForward: THREE.Vector3 = new THREE.Vector3();

let camera: THREE.PerspectiveCamera | null = null;
let cameraController: SceneCameraController | null = null;
let previewModel : THREE.Object3D | undefined = undefined;

const initialCameraPosition: THREE.Vector3 = new THREE.Vector3();
const initialCameraRotation: THREE.Euler = new THREE.Euler();
const initialModelPosition: THREE.Vector3 = new THREE.Vector3();
const initialModelRotation: THREE.Euler = new THREE.Euler();

let actionIndexWhenOpened: number = -1;

export default function ModelTrackerPoseEditor(props: ModelTrackerPoseEditorProps) 
{
	const forceUpdate = useForceUpdate();

	const currentDisplayDevice: IDisplayDeviceInfo = useTypedSelector(state => state.visageModule.currentDisplayDevice);

	const canvasHeight: number = 512;
	const canvasWidth: number = canvasHeight * props.tracker.modelTrackerCamera.aspect;
	const [modelInView, setModelInView] = useState<boolean>(true);

	useEventListener(CustomEventTypes.onGizmoSpaceChanged, () => forceUpdate(), document);
	useEventListener(CustomEventTypes.onGizmoPivotModeChanged, () => forceUpdate(), document);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);

	useEffect(() => 
	{
		camera = props.tracker.modelTrackerCamera;
		previewModel = props.tracker.modelPreviewInstance;
		cameraController = props.tracker.modelTrackerCameraController;

		camera.updateMatrix();
		camera.updateMatrixWorld(true);

		assert(camera.userData.anchorDistance);

		camera.getWorldDirection(cameraForward);
		cameraController.getAnchor().addVectors(
			camera.position,
			cameraForward.multiplyScalar(camera.userData.anchorDistance)
		);

		initialCameraPosition.copy(camera.position);
		initialCameraRotation.copy(camera.rotation);
		if ( previewModel )
		{
			initialModelPosition.copy(previewModel.position);
			initialModelRotation.copy(previewModel.rotation);
		}

		actionIndexWhenOpened = ActionManager.currentActionIndex;

		const gizmoManager = Engine.getInstance().getGizmoManager();
		gizmoManager.setScene(props.tracker.posePreviewScene);
		if ( previewModel )
		{
			gizmoManager.setObjects([previewModel]);
		}
		gizmoManager.setSize(0.75);
		if (gizmoManager.getMode() === GizmoModes.Scale) 
		{
			gizmoManager.setMode(GizmoModes.Rotate);
		}
		WorkspaceManager.setModelTrackerPoseEditorData({
			updateFunction: update,
			context: canvasRef.current!.getContext('2d')!,
			tracker: props.tracker,
		});
		gizmoManager.setQuickDuplicationEnabled(false);

		return () => 
		{
			camera!.userData.anchorDistance = cameraController!.getDistanceToAnchor();

			const currentScene = Engine.getInstance().getCurrentScene();
			if ( currentScene )
			{
				gizmoManager.setScene(currentScene);
			}
			gizmoManager.setObjects([props.tracker]);
			gizmoManager.setSize(1.0);

			WorkspaceManager.setModelTrackerPoseEditorData({ context: null });
			gizmoManager.setQuickDuplicationEnabled(true);
		};
	}, []);

	function update() 
	{
		const gizmoManager = Engine.getInstance().getGizmoManager();
		if (!gizmoManager.isBusy()) 
		{
			if (Input.getKeybindDown(Input.KeyBindings.GizmoModeTranslate)) 
			{
				gizmoManager.setMode(GizmoModes.Translate);
			}
			else if (Input.getKeybindDown(Input.KeyBindings.GizmoModeRotate)) 
			{
				gizmoManager.setMode(GizmoModes.Rotate);
			}
			else if (Input.getKeybindDown(Input.KeyBindings.GizmoModeSpaceToggle)) 
			{
				gizmoManager.setSpace(gizmoManager.getSpace() === Space.Local ? Space.World : Space.Local);
			}
			else if (Input.getKeybindDown(Input.KeyBindings.GizmoHide)) 
			{
				gizmoManager.setMode(GizmoModes.Hidden);
			}
		}
		if (Input.getKeybindDown(Input.KeyBindings.CameraFocus)) 
		{
			if ( cameraController && previewModel )
			{
				cameraController.focus([previewModel]);
			}
		}
		if (Input.getKeybindDown(Input.KeyBindings.CameraRestoreOrientation)) 
		{
			resetPose();
		}
		if (
			Input.getKeybindDown(Input.KeyBindings.Redo) ||
			Input.getKeybindDown(Input.KeyBindings.Redo2)
		) 
		{
			ActionManager.redo();
		}
		else if (
			ActionManager.currentActionIndex > actionIndexWhenOpened &&
			Input.getKeybindDown(Input.KeyBindings.Undo)
		) 
		{
			ActionManager.undo();
		}

		cameraController!.update();
		camera!.updateMatrixWorld();
		projectionMat.multiplyMatrices(
			camera!.projectionMatrix,
			new THREE.Matrix4().copy(camera!.matrixWorld).invert()
		);
		frustum.setFromProjectionMatrix(projectionMat);
		setModelInView(frustum.intersectsObject(previewModel!));
	}

	function resetPose() 
	{
		props.tracker.resetPose();
		Engine.getInstance().getGizmoManager().setObjects([previewModel!]);
	}

	function onApply() 
	{
		ActionManager.removeAllTemporaryActions();
		ModalManager.removeModal(props.getEditPoseModalId());
		setTimeout(() => 
		{
			new SetModelTrackerPoseAction(
				props.tracker,
				camera!.position.clone(),
				initialCameraPosition.clone(),
				camera!.rotation.clone(),
				initialCameraRotation.clone(),
				previewModel!.position.clone(),
				initialModelPosition.clone(),
				previewModel!.rotation.clone(),
				initialModelRotation.clone()
			);
		}, 0);
	}

	function onCancel() 
	{
		ActionManager.removeAllTemporaryActions();
		ModalManager.removeModal(props.getEditPoseModalId());
		setTimeout(() => 
		{
			camera!.position.copy(initialCameraPosition);
			camera!.rotation.copy(initialCameraRotation);
			previewModel!.position.copy(initialModelPosition);
			previewModel!.rotation.copy(initialModelRotation);
			camera!.userData.renderIsDirty = true;
		}, 0);
	}

	return (
		<div>
			<Panel orientation={LayoutOrientations.Horizontal}>
				<Spacer stretchTo={{ x: '100%' }} />
				<div className="sceneStatusContent" style={{ fontSize: '20px' }}>
					{'Navigate camera to the desired pose'}
				</div>
				<Spacer stretchTo={{ x: '100%' }} />
			</Panel>
			<div data-acceptmouseinput="true" style={{ textAlign: 'center' }}>
				<div className={"centerContent"}
					style={{ position: "relative" }}
				>
					<canvas
						ref={canvasRef}
						width={canvasWidth}
						height={canvasHeight}
						style={{
							width: canvasWidth + 'px',
							height: canvasHeight + 'px',
							border: '1px solid #000',
						}}
						data-viewport={true}
					/>
					{ 	
						currentDisplayDevice.id === GrapesDeviceTypes.HoloLens2 && 
							<HoloLensVisorOverlay 
								// size and position calculated by hand to look good since the canvas aspect is set by the camera and not the display device
								// see https://scopearcloud.atlassian.net/browse/WC20-4880 for more details
								overlayWidth={435}
								overlayHeight={235}
								verticalOffset={42}
							/> 
					}
				</div>
				{!modelInView && (
					<Panel orientation={LayoutOrientations.Horizontal}>
						<Spacer stretchTo={{ x: '100%' }} />
						<div
							className="sceneStatusContent"
							style={{ fontSize: '20px' }}
							data-status="EDIT-POSE ERROR"
						>
							{'Invalid Pose'}
						</div>
						<Spacer stretchTo={{ x: '100%' }} />
					</Panel>
				)}
				<Spacer size={{ y: '10px' }} />
				<div className="toolbar centerContent">
					<ButtonGroup
						radio={true}
						value={['hidden', 'translate', 'rotate', 'scale'].indexOf(
							Engine.getInstance().getGizmoManager().getMode()
						)}
						data-name="gizmo-type-group"
					>
						<Button
							iconRenderer={<GizmoNoneIcon scale={0.8} />}
							className="toolbarButton"
							data-name="gizmo-type-hidden"
							onClick={() => 
							{
								Engine.getInstance().getGizmoManager().setMode(GizmoModes.Hidden);
							}}
							tooltip={TooltipKeys.GizmoHidden}
						/>
						<Button
							iconRenderer={<GizmoMoveIcon scale={0.8} />}
							className="toolbarButton"
							data-name="gizmo-type-translate"
							onClick={() => 
							{
								Engine.getInstance().getGizmoManager().setMode(GizmoModes.Translate);
							}}
							tooltip={TooltipKeys.GizmoMove}
						/>
						<Button
							iconRenderer={<Rotate3dIcon scale={0.8} />}
							className="toolbarButton"
							data-name="gizmo-type-rotate"
							onClick={() => 
							{
								Engine.getInstance().getGizmoManager().setMode(GizmoModes.Rotate);
							}}
							tooltip={TooltipKeys.GizmoRotate}
						/>
					</ButtonGroup>
					<Spacer size={{ x: '12px' }} />
					<ButtonGroup
						radio={true}
						value={['world', 'local'].indexOf(
							Engine.getInstance().getGizmoManager().getSpace()
						)}
						data-name="gizmo-space-group"
					>
						<Button
							iconRenderer={<SpaceWorldIcon scale={0.8} />}
							className="toolbarButton"
							data-name="set-space-world"
							onClick={() => 
							{
								Engine.getInstance().getGizmoManager().setSpace(Space.World);
							}}
							tooltip={TooltipKeys.GizmoSpaceWorld}
						/>
						<Button
							iconRenderer={<SpaceLocalIcon scale={0.8} />}
							className="toolbarButton"
							data-name="set-space-local"
							onClick={() => 
							{
								Engine.getInstance().getGizmoManager().setSpace(Space.Local);
							}}
							tooltip={TooltipKeys.GizmoSpaceLocal}
						/>
					</ButtonGroup>
					<Spacer size={{ x: '12px' }} />
					<Button
						iconRenderer={<TargetIcon scale={0.8} />}
						data-name="camera-control-button-focus"
						className="toolbarButton"
						onClick={() => 
						{
							if ( cameraController && previewModel)
							{
								cameraController.focus([previewModel]);
							}
						}}
						tooltip={TooltipKeys.CameraFocusButton}
					/>
					<Button
						iconRenderer={<HomeIcon scale={0.8} />}
						data-name="camera-control-button-home"
						className="toolbarButton"
						onClick={resetPose}
						tooltip={TooltipKeys.CameraHomeButton}
					/>
				</div>
				<Spacer size={{ y: '10px' }} />
				<div className="layoutPanel horizontal centerContent">
					<Button data-name="cancel-edit-pose" label="Cancel" onClick={onCancel} />
					<Spacer size={{ x: '20px' }} />
					<Button
						data-name="apply-edit-pose"
						label="Apply"
						onClick={onApply}
						disabled={!modelInView}
						className="inverse"
					/>
				</div>
				<Spacer size={{ y: '10px' }} />
			</div>
		</div>
	);
}
